import { api } from "@/lib/api";
import type {
	IdVerification,
	User,
	UserFilters,
	UsersListResponse,
	UserUpdateDto,
} from "@/types/admin";

// Users API Service
export const usersService = {
	// Get paginated list of users
	getUsers: async (
		page = 1,
		limit = 50,
		filters?: UserFilters,
	): Promise<UsersListResponse> => {
		const params = {
			page,
			limit,
			...filters,
		};

		const response = await api.get<UsersListResponse>("/admin/users", params);
		if (!response.data.success || !response.data.data) {
			throw new Error("Failed to fetch users");
		}
		return response.data.data;
	},

	// Get user by ID
	getUser: async (userId: string): Promise<User> => {
		const response = await api.get<User>(`/admin/users/${userId}`);
		if (!response.data.success || !response.data.data) {
			throw new Error("Failed to fetch user");
		}
		return response.data.data as User;
	},

	// Update user
	updateUser: async (userId: string, data: UserUpdateDto): Promise<User> => {
		const response = await api.patch<User>(`/admin/users/${userId}`, data);
		if (!response.data.success || !response.data.data) {
			throw new Error("Failed to update user");
		}
		return response.data.data as User;
	},

	// Suspend user account
	suspendUser: async (userId: string, reason?: string): Promise<User> => {
		const response = await api.post<User>(`/admin/users/${userId}/suspend`, {
			reason,
		});
		if (!response.data.success || !response.data.data) {
			throw new Error("Failed to suspend user");
		}
		return response.data.data as User;
	},

	// Approve user
	approveUser: async (userId: string): Promise<User> => {
		const response = await api.post<User>(`/admin/users/${userId}/approve`);
		if (!response.data.success || !response.data.data) {
			throw new Error("Failed to approve user");
		}
		return response.data.data as User;
	},

	// Reject user
	rejectUser: async (userId: string, reason: string): Promise<User> => {
		const response = await api.post<User>(`/admin/users/${userId}/reject`, {
			reason,
		});
		if (!response.data.success || !response.data.data) {
			throw new Error("Failed to reject user");
		}
		return response.data.data as User;
	},

	// Get users pending approval
	getPendingApprovalUsers: async (): Promise<User[]> => {
		const response = await api.get<User[]>("/admin/users/pending-approval");
		if (!response.data.success || !response.data.data) {
			throw new Error("Failed to fetch pending approval users");
		}
		return response.data.data as User[];
	},

	// Get users with pending ID verification
	getPendingIdVerificationUsers: async (): Promise<IdVerification[]> => {
		const response = await api.get<IdVerification[]>(
			"/admin/users/pending-id-verification",
		);
		if (!response.data.success || !response.data.data) {
			throw new Error("Failed to fetch pending ID verification users");
		}
		return response.data.data as IdVerification[];
	},

	// Verify user ID (approve or reject)
	verifyUserId: async (
		userId: string,
		approved: boolean,
		rejectionReason?: string,
	): Promise<User> => {
		const response = await api.post<User>(`/admin/users/${userId}/verify-id`, {
			approved,
			rejectionReason,
		});
		if (!response.data.success || !response.data.data) {
			throw new Error("Failed to verify user ID");
		}
		return response.data.data as User;
	},

	// Activate user (set status to active)
	activateUser: async (userId: string): Promise<User> => {
		return usersService.updateUser(userId, { status: "active" });
	},

	// Deactivate user (set status to suspended)
	deactivateUser: async (userId: string, reason?: string): Promise<User> => {
		return usersService.suspendUser(userId, reason);
	},

	// Reset user counters (not implemented in backend, placeholder)
	resetCounters: async (userId: string): Promise<User> => {
		// This is a placeholder - backend doesn't have this endpoint yet
		// Return the user as-is for now
		return usersService.getUser(userId);
	},
};
